import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { RealtimePill } from '@/components/RealtimePill';
import { NotificationsBell } from '@/components/NotificationsBell';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { HelpButton } from '@/components/HelpButton';
import { UserAvatar } from '@/components/common/UserAvatar';
import { SectionGuide } from '@/components/common/SectionGuide';
import { getSectionGuide } from '@/lib/sectionGuides';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  LayoutDashboard, Users, Clock, FileText, HelpCircle, Newspaper,
  CalendarDays, Phone, Menu, LogOut, Mountain, Package, User, Settings as SettingsIcon,
  ChevronDown, LayoutGrid, Eye, FolderTree, Tag, MessageSquare, PenLine, Images, Share2, Mail, Send, Users2, LayoutTemplate, BarChart3,
  PanelLeftClose, PanelLeft, Briefcase, Wrench, MapPin, CalendarClock, ShieldCheck, Wallet,
  Hotel, UtensilsCrossed, ShoppingBag, Sparkles, BedDouble, ShoppingCart, CreditCard, RotateCcw, Store, BookOpen,
  Map, Bike, ListChecks, Wine, Wheat, Palette, QrCode, Truck, Headset, AlertTriangle, Ticket, ScanLine, Leaf, TreePine,
  Boxes, Receipt, Star, Percent, Globe, Radar, Plug, Fingerprint, Cookie, Bot, Rocket, ClipboardCheck, Megaphone, Home, GraduationCap,
  HeartPulse, ConciergeBell,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Sidebar palette (dark) — requested color #14171F
const SIDEBAR_BG = 'bg-[#14171F]';
const SIDEBAR_TEXT = 'text-slate-300';
const SIDEBAR_BORDER = 'border-white/10';

const BLOG_CHILDREN = [
  { to: '/admin/blog', label: 'Panoramica', icon: LayoutGrid, end: true },
  { to: '/admin/blog/post', label: 'Post', icon: FileText },
  { to: '/admin/blog/anteprima', label: 'Anteprima', icon: Eye },
  { to: '/admin/blog/categorie', label: 'Categorie', icon: FolderTree },
  { to: '/admin/blog/tag', label: 'Tag', icon: Tag },
  { to: '/admin/blog/commenti', label: 'Commenti', icon: MessageSquare },
  { to: '/admin/blog/redattori', label: 'Redattori', icon: PenLine },
];

const NEWSLETTER_CHILDREN = [
  { to: '/admin/newsletter', label: 'Campagne', icon: Send, end: true },
  { to: '/admin/newsletter/iscritti', label: 'Iscritti', icon: Users2 },
  { to: '/admin/newsletter/template', label: 'Template', icon: LayoutTemplate },
  { to: '/admin/newsletter/statistiche', label: 'Statistiche', icon: BarChart3 },
];

const STAFF_CHILDREN = [
  { to: '/admin/staff', label: 'Gestione staff', icon: Briefcase, end: true },
  { to: '/admin/staff/servizi', label: 'Servizi', icon: Wrench },
  { to: '/admin/staff/zone', label: 'Zone', icon: MapPin },
  { to: '/admin/staff/calendario', label: 'Calendario', icon: CalendarClock },
  { to: '/admin/staff/email', label: 'Template Email', icon: Mail },
];

const PAGES_CHILDREN = [
  { to: '/admin/pagine/la-sila', label: 'La Sila', icon: Mountain },
  { to: '/admin/pagine/il-parco-della-sila', label: 'Il Parco della Sila', icon: Map },
  { to: '/admin/pagine/experience', label: 'Experience', icon: FileText },
  { to: '/admin/pagine/il-progetto', label: 'Il Progetto', icon: FileText },
  { to: '/admin/pagine/il-progetto/bilanci', label: 'Bilanci (Progetto)', icon: Receipt },
  { to: '/admin/pagine/chi-siamo', label: 'Chi siamo', icon: Users },
  { to: '/admin/pagine/chi-sono', label: 'Chi sono', icon: User },
  { to: '/admin/pagine/dove-mangiare', label: 'Dove Mangiare', icon: UtensilsCrossed },
  { to: '/admin/pagine/alloggi', label: 'Alloggi', icon: BedDouble },
  { to: '/admin/pagine/servizi', label: 'Servizi', icon: FileText },
  { to: '/admin/pagine/parchi', label: 'Parchi', icon: FileText },
  { to: '/admin/pagine/tour-nei-borghi', label: 'Tour nei Borghi', icon: MapPin },
  { to: '/admin/pagine/shop', label: 'Shop', icon: ShoppingBag },
  { to: '/admin/pagine/eventi', label: 'Eventi', icon: CalendarDays },
  { to: '/admin/pagine/corsi-online', label: 'Corsi Online', icon: BookOpen },
  { to: '/admin/pagine/in-sila', label: 'In Sila (presenza)', icon: BookOpen },
  { to: '/admin/pagine/lavora-con-noi', label: 'Lavora con noi', icon: Briefcase },
  { to: '/admin/pagine/termini', label: 'Termini e Condizioni', icon: FileText },
  { to: '/admin/pagine/privacy-sicurezza', label: 'Privacy & Sicurezza', icon: ShieldCheck },
];

const ADMIN_EXPERIENCE_CHILDREN = [
  { to: '/admin/esperienze', label: 'Catalogo', icon: Ticket, end: true },
  { to: '/admin/esperienze/richieste', label: 'Richieste modulo', icon: Rocket },
  { to: '/admin/esperienze/vetrina', label: 'Vetrina', icon: Store },
  { to: '/admin/esperienze/iscrizioni', label: 'Iscrizioni', icon: ListChecks },
  { to: '/admin/esperienze/check-in', label: 'Check-in', icon: ScanLine },
  { to: '/admin/esperienze/analytics', label: 'Analytics', icon: BarChart3 },
];

const ADMIN_NAV = [
  { group: 'Panoramica', items: [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/admin/analytics', label: 'Andamento Sito', icon: Radar },
    { to: '/admin/cets', label: 'CETS · Indicatori', icon: Leaf },
    { to: '/admin/cets-domande', label: 'CETS · Domande', icon: ListChecks },
    { to: '/admin/cets-risposte', label: 'CETS · Risposte', icon: TreePine },
  ] },
  { group: 'Stakeholder', items: [
    { to: '/admin/stakeholders', label: 'Gestione stakeholder', icon: Users },
    { to: '/admin/pending', label: 'Richieste in attesa', icon: Clock },
  ]},
  { group: 'Clienti', items: [
    { to: '/admin/clienti', label: 'Salute Clienti', icon: HeartPulse },
  ]},
  { group: 'Immobiliare', items: [
    { to: '/admin/immobili', label: 'Case in vendita', icon: Home },
  ]},
  { group: 'Partners', items: [
    { to: '/admin/investitori', label: 'Partners', icon: BarChart3 },
  ]},
  { group: 'Tarapoto', items: [
    { to: '/admin/tarapoto', label: 'Tarapoto Experiences', icon: Leaf },
  ]},
  { group: 'Staff', items: [
    { label: 'Staff', icon: Briefcase, children: STAFF_CHILDREN },
  ]},
  { group: 'Contenuti', items: [
    { to: '/admin/home', label: 'Home', icon: FileText },
    { label: 'Pagine sito', icon: LayoutGrid, children: PAGES_CHILDREN },
    { label: 'Blog', icon: Newspaper, children: BLOG_CHILDREN },
    { label: 'Newsletter', icon: Mail, children: NEWSLETTER_CHILDREN },
    { to: '/admin/galleria', label: 'Galleria', icon: Images },
    { to: '/admin/social', label: 'Social', icon: Share2 },
    { to: '/admin/faq', label: 'FAQ', icon: HelpCircle },
    { label: 'Esperienze', icon: Ticket, children: ADMIN_EXPERIENCE_CHILDREN },
    { to: '/admin/eventi', label: 'Eventi', icon: CalendarDays },
    { to: '/admin/servizi', label: 'Servizi', icon: ConciergeBell },
    { to: '/admin/corsi', label: 'Corsi', icon: GraduationCap },
    { to: '/admin/contatti', label: 'Contatti', icon: Phone },
  ]},
  { group: 'Parco', items: [
    { to: '/admin/parco', label: 'Parco', icon: Mountain },
    { to: '/admin/parco/guide', label: 'Guide', icon: Users2 },
    { to: '/admin/prenotazioni', label: 'Prenotazioni', icon: CalendarClock },
  ]},
  { group: 'Sistema', items: [
    { to: '/admin/incassi', label: 'Incassi & Pagamenti', icon: Wallet },
    { to: '/admin/carrello', label: 'Carrello & Checkout', icon: ShoppingCart },
    { to: '/admin/tessera', label: 'Tessera & Commissioni', icon: CreditCard },
    { to: '/admin/seo', label: 'SEO', icon: Globe },
    { to: '/admin/cookie', label: 'Cookie & Consensi', icon: Cookie },
    { to: '/admin/assistente', label: 'Assistente AI', icon: Bot },
    { to: '/admin/connessioni', label: 'Connessioni esterne', icon: Plug },
    { to: '/admin/email', label: 'Email di piattaforma', icon: Mail },
    { to: '/admin/audit', label: 'Audit accessi', icon: ShieldCheck },
    { to: '/admin/sicurezza', label: 'Sicurezza', icon: Fingerprint },
    { to: '/admin/impostazioni', label: 'Impostazioni', icon: SettingsIcon },
  ]},
];

// Voce verticale dinamica in base al tipo di stakeholder scelto in registrazione
const VERTICAL_META = {
  hotel: { label: 'Ospitalità', icon: Hotel },
  restaurant: { label: 'Ristorante', icon: UtensilsCrossed },
  ecommerce: { label: 'Ecommerce', icon: ShoppingBag },
  experience: { label: 'Experience', icon: Sparkles },
  tour: { label: 'Tour', icon: Map },
  guida: { label: 'Guida del Parco', icon: Map },
  servizi: { label: 'Servizi / Noleggi', icon: ConciergeBell },
  eventi: { label: 'Eventi', icon: CalendarDays },
  educational: { label: 'Educational', icon: GraduationCap },
};

const HOTEL_CHILDREN = [
  { to: '/area/hotel/camere', label: 'Camere', icon: BedDouble },
  { to: '/area/hotel/anteprima', label: 'Anteprima', icon: Eye },
  { to: '/area/hotel/vetrina', label: 'Vetrina', icon: Store },
  { to: '/area/hotel/calendario', label: 'Calendario', icon: CalendarDays },
  { to: '/area/hotel/prenotazioni', label: 'Prenotazioni', icon: CalendarClock },
  { to: '/area/hotel/consegne', label: 'Consegna Immobile', icon: ClipboardCheck },
  { to: '/area/hotel/messaggi', label: 'Messaggi Ospiti', icon: MessageSquare },
  { to: '/area/hotel/servizi-extra', label: 'Servizi Extra', icon: Sparkles },
  { to: '/area/hotel/profitto', label: 'Profitto', icon: BarChart3 },
  { to: '/area/hotel/rimborsi', label: 'Rimborsi', icon: RotateCcw },
  { to: '/area/hotel/email-templates', label: 'Email Templates', icon: Mail },
  { to: '/area/gallery', label: 'Galleria', icon: Images },
  { to: '/area/hotel/checkout', label: 'Checkout UI', icon: ShoppingCart },
  { to: '/area/hotel/pagamenti', label: 'Pagamenti', icon: CreditCard },
  { to: '/area/hotel/impostazioni', label: 'Impostazioni', icon: SettingsIcon },
  { to: '/area/hotel/guida', label: 'Guida', icon: HelpCircle },
];

// Menu verticale Ristorante — un UNICO contenitore "Ristorante" a scomparsa (identico ad Hotel):
// tutte le voci del ristorante sono figlie dirette di questa categoria.
const RESTAURANT_CHILDREN = [
  { to: '/area/restaurant', label: 'Panoramica', icon: LayoutGrid, end: true },
  { to: '/area/restaurant/vetrina', label: 'Vetrina', icon: Store },
  { to: '/area/restaurant/piantina', label: 'Piantina', icon: Map },
  { to: '/area/restaurant/camerieri', label: 'Camerieri', icon: Users },
  { to: '/area/restaurant/rider', label: 'Rider', icon: Bike },
  { to: '/area/restaurant/prenotazioni', label: 'Prenotazioni', icon: CalendarClock },
  { to: '/area/restaurant/recensioni', label: 'Recensioni', icon: Star },
  { to: '/area/restaurant/orari-turni', label: 'Orari e Turni', icon: Clock },
  { to: '/area/restaurant/pagamenti', label: 'Cassa', icon: Wallet },
  { to: '/area/restaurant/carta-digitale', label: 'Carta digitale', icon: QrCode },
  { to: '/area/restaurant/menu', label: 'Menu', icon: BookOpen },
  { to: '/area/restaurant/vini', label: 'Vini', icon: Wine },
  { to: '/area/restaurant/menu?tab=allergens', label: 'Allergeni', icon: Wheat },
  { to: '/area/gallery', label: 'Galleria', icon: Images },
  { to: '/area/restaurant/personalizzazione', label: 'Personalizzazione', icon: Palette },
  { to: '/area/restaurant/canali-vendita', label: 'Canali di vendita', icon: Store },
  { to: '/area/restaurant/delivery-hubrise', label: 'Delivery & HubRise', icon: Truck },
  { to: '/area/restaurant/qr-code', label: 'QR Code', icon: QrCode },
  { to: '/area/restaurant/qr-personalizzazione', label: 'QR Personalizzazione', icon: Palette },
  { to: '/area/restaurant/logistica', label: 'Logistica', icon: Package },
  { to: '/area/restaurant/moduli', label: 'Moduli', icon: FileText },
  { to: '/area/restaurant/customer-care', label: 'Customer care', icon: Headset },
  { to: '/area/restaurant/messaggi', label: 'Messaggi', icon: MessageSquare },
  { to: '/area/restaurant/email', label: 'Email', icon: Mail },
  { to: '/area/restaurant/reclami', label: 'Reclami', icon: AlertTriangle },
  { to: '/area/restaurant/impostazioni', label: 'Impostazioni', icon: SettingsIcon },
];

const EXPERIENCE_CHILDREN = [
  { to: '/area/experience', label: 'Dashboard', icon: LayoutGrid, end: true },
  { to: '/area/experience/esperienze', label: 'Esperienze', icon: Ticket },
  { to: '/area/experience/vetrina', label: 'Vetrina', icon: Store },
  { to: '/area/experience/iscrizioni', label: 'Iscrizioni', icon: ListChecks },
  { to: '/area/experience/check-in', label: 'Check-in', icon: ScanLine },
  { to: '/area/experience/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/area/experience/recensioni', label: 'Recensioni', icon: Star },
  { to: '/area/gallery', label: 'Galleria', icon: Images },
];

// Area TOUR (stakeholder tenant_type === 'tour') — riusa il MOTORE Experiences
// (biglietti, prenotazioni, check-in, analytics, vetrina) con branding "Tour dei Borghi".
const TOUR_CHILDREN = [
  { to: '/area/experience', label: 'Dashboard', icon: LayoutGrid, end: true },
  { to: '/area/experience/esperienze', label: 'I miei Tour', icon: Map },
  { to: '/area/experience/vetrina', label: 'Vetrina', icon: Store },
  { to: '/area/experience/iscrizioni', label: 'Prenotazioni', icon: ListChecks },
  { to: '/area/experience/check-in', label: 'Check-in', icon: ScanLine },
  { to: '/area/experience/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/area/experience/recensioni', label: 'Recensioni', icon: Star },
  { to: '/area/gallery', label: 'Galleria', icon: Images },
];
// sezioni con voci dirette (stile ADMIN_NAV). Le funzioni specifiche verranno
// organizzate DENTRO ogni pagina tramite tab/sottosezioni/drawer/modali.
const ECOMMERCE_NAV = [
  { group: 'Panoramica', items: [
    { to: '/area/ecommerce', label: 'Dashboard', icon: LayoutDashboard, end: true },
  ]},
  { group: 'Catalogo', items: [
    { to: '/area/ecommerce/prodotti', label: 'Prodotti', icon: Package },
    { to: '/area/ecommerce/inventario', label: 'Inventario', icon: Boxes },
  ]},
  { group: 'Vendite', items: [
    { to: '/area/ecommerce/ordini', label: 'Ordini', icon: ShoppingCart },
    { to: '/area/ecommerce/clienti', label: 'Clienti e Preventivi', icon: Users },
  ]},
  { group: 'Logistica', items: [
    { to: '/area/ecommerce/spedizioni', label: 'Spedizioni e Resi', icon: Truck },
  ]},
  { group: 'Amministrazione', items: [
    { to: '/area/ecommerce/pagamenti', label: 'Pagamenti e Fatture', icon: Receipt },
    { to: '/area/ecommerce/tasse', label: 'Tasse e Profitto', icon: Percent },
  ]},
  { group: 'Marketing', items: [
    { to: '/area/ecommerce/promozioni', label: 'Promozioni', icon: Percent },
    { to: '/area/ecommerce/recensioni', label: 'Recensioni', icon: Star },
  ]},
  { group: 'Vetrina', items: [
    { to: '/area/ecommerce/vetrina', label: 'Vetrina', icon: Store },
  ]},
  { group: 'Strumenti', items: [
    { to: '/area/gallery', label: 'Galleria', icon: Images },
    { to: '/area/contatti', label: 'Contatti', icon: Phone },
  ]},
  { group: 'Configurazione', items: [
    { to: '/area/ecommerce/impostazioni', label: 'Impostazioni e Integrazioni', icon: SettingsIcon },
    { to: '/area/pagamenti', label: 'Pagamenti', icon: CreditCard },
    { to: '/area/sicurezza', label: 'Sicurezza', icon: Fingerprint },
  ]},
];

function buildStakeholderNav(tenantType) {
  if (tenantType === 'ecommerce') {
    return [
      ...ECOMMERCE_NAV,
      { group: 'Moduli', items: [
        { to: '/area/eventi', label: 'Eventi', icon: CalendarDays },
        { to: '/area/servizi', label: 'Servizi', icon: ConciergeBell },
        { to: '/area/corsi', label: 'Corsi', icon: GraduationCap },
        { to: '/area/esperienze', label: 'Esperienze', icon: Sparkles },
        { to: '/area/cets', label: 'Sostenibilità CETS', icon: TreePine },
        { to: '/area/avvisi', label: 'Avviso Vetrina', icon: Megaphone },
      ] },
    ];
  }
  const vert = VERTICAL_META[tenantType];
  const areaItems = [{ to: '/area', label: 'Dashboard', icon: LayoutDashboard, end: true }];
  if (tenantType === 'hotel') {
    areaItems.push({ label: 'Ospitalità', icon: Hotel, children: HOTEL_CHILDREN });
  } else if (tenantType === 'restaurant') {
    areaItems.push({ label: 'Ristorante', icon: UtensilsCrossed, children: RESTAURANT_CHILDREN });
  } else if (tenantType === 'experience') {
    areaItems.push({ label: 'Experience', icon: Sparkles, children: EXPERIENCE_CHILDREN });
  } else if (tenantType === 'tour') {
    areaItems.push({ label: 'Tour dei Borghi', icon: Map, children: TOUR_CHILDREN });
  } else if (tenantType === 'guida') {
    areaItems.push({ to: '/area/parco', label: 'I miei itinerari', icon: Map });
  } else if (tenantType === 'servizi' || tenantType === 'eventi' || tenantType === 'educational') {
    // Operatori a modulo singolo (Servizi / Eventi / Educational): nessuna area verticale,
    // usano solo il proprio modulo trasversale (agganciato al CRM già esistente).
  } else if (vert) {
    areaItems.push({ to: '/area/contenuti', label: vert.label, icon: vert.icon });
  }
  // Modulo Eventi trasversale: tenant abilitati + operatore dedicato "eventi".
  if (['hotel', 'restaurant', 'experience', 'tour', 'eventi'].includes(tenantType)) {
    areaItems.push({ to: '/area/eventi', label: 'Eventi', icon: CalendarDays });
  }
  // Modulo Corsi / Educational trasversale: tenant abilitati + operatore dedicato "educational".
  if (['hotel', 'restaurant', 'experience', 'tour', 'guida', 'educational'].includes(tenantType)) {
    areaItems.push({ to: '/area/corsi', label: tenantType === 'educational' ? 'Educational' : 'Corsi', icon: GraduationCap });
  }
  // Modulo Esperienze trasversale (self-service + approvazione admin): per i tenant NON nativi.
  // experience/tour hanno già la loro area completa qui sopra.
  if (['hotel', 'restaurant'].includes(tenantType)) {
    areaItems.push({ to: '/area/esperienze', label: 'Esperienze', icon: Sparkles });
  }
  // Questionario CETS + rating vetrina: hotel/restaurant/experience (esenti guida/tour).
  if (['hotel', 'restaurant', 'experience'].includes(tenantType)) {
    areaItems.push({ to: '/area/cets', label: 'Sostenibilità CETS', icon: TreePine });
  }
  // Avviso di vetrina (banner scorrevole): per i tenant con vetrina pubblica.
  if (['hotel', 'restaurant', 'experience'].includes(tenantType)) {
    areaItems.push({ to: '/area/avvisi', label: 'Avviso Vetrina', icon: Megaphone });
  }
  // Modulo Servizi (utility: navetta, auto, legna, gas, assicurazione…): disponibile a tutti
  // gli stakeholder TRANNE gli operatori dedicati Eventi/Educational (hanno il proprio modulo).
  if (!['eventi', 'educational'].includes(tenantType)) {
    areaItems.push({ to: '/area/servizi', label: 'Servizi', icon: ConciergeBell });
  }
  return [
    { group: 'Area', items: areaItems },
    { group: 'Strumenti', items: [
      { to: '/area/gallery', label: 'Gallery', icon: Images },
      { to: '/area/contatti', label: 'Contatti', icon: Phone },
      { to: '/area/pagamenti', label: 'Pagamenti', icon: CreditCard },
      { to: '/area/sicurezza', label: 'Sicurezza', icon: Fingerprint },
    ]},
  ];
}

const STAFF_NAV = [
  { group: 'Area Staff', items: [
    { to: '/staff-area', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/staff-area/profilo', label: 'Profilo', icon: User },
    { to: '/staff-area/sicurezza', label: 'Sicurezza', icon: Fingerprint },
    { to: '/staff-area/verifica', label: 'Verifica identità', icon: ShieldCheck },
  ]},
  { group: 'Prossimamente', items: [
    { to: '/staff-area/incarichi', label: 'Incarichi', icon: Briefcase },
    { to: '/staff-area/calendario', label: 'Calendario', icon: CalendarClock },
    { to: '/staff-area/pagamenti', label: 'Pagamenti', icon: Wallet },
  ]},
];

// Portale Partners (Investitori) — SOLA LETTURA.
const INVESTOR_NAV = [
  { group: 'Area Partners', items: [
    { to: '/investitori', label: 'Andamento investimenti', icon: BarChart3, end: true },
    { to: '/investitori/documenti', label: 'Documenti', icon: FileText },
    { to: '/investitori/sicurezza', label: 'Sicurezza', icon: Fingerprint },
  ]},
];

const linkClass = ({ isActive }) => cn(
  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
  isActive ? 'bg-white/10 text-white shadow-sm' : 'text-slate-300 hover:bg-white/5 hover:text-white'
);

function ExpandableItem({ item, onNavigate, collapsed, onExpandRequest }) {
  const location = useLocation();
  const childActive = item.children.some((c) => location.pathname === c.to || (!c.end && location.pathname.startsWith(c.to)));
  const [open, setOpen] = useState(childActive);
  const Icon = item.icon;
  const slug = item.label.toLowerCase().replace(/[^a-z]+/g, '-');

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" data-testid={`nav-${slug}-toggle`} onClick={() => { onExpandRequest?.(); setOpen(true); }}
            className={cn('flex w-full items-center justify-center rounded-lg px-3 py-2.5 transition-colors',
              childActive ? 'bg-white/10 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white')}>
            <Icon className="h-5 w-5 shrink-0" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`nav-${slug}-toggle`}
        className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          childActive ? 'text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white')}>
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className={cn('ml-4 mt-1 flex flex-col gap-1 border-l pl-3', SIDEBAR_BORDER)}>
          {item.children.map((c) => {
            const CIcon = c.icon;
            return (
              <NavLink key={c.to} to={c.to} end={c.end} onClick={onNavigate}
                data-testid={`nav-${slug}-${c.label.toLowerCase()}`} className={linkClass}>
                <CIcon className="h-4 w-4 shrink-0" />
                <span>{c.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CollapsedLink({ item, onNavigate }) {
  const Icon = item.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink to={item.to} end={item.end} onClick={onNavigate}
          data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
          className={({ isActive }) => cn('flex items-center justify-center rounded-lg px-3 py-2.5 transition-colors',
            isActive ? 'bg-white/10 text-white shadow-sm' : 'text-slate-300 hover:bg-white/5 hover:text-white')}>
          <Icon className="h-5 w-5 shrink-0" />
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

function NavContent({ nav, onNavigate, collapsed, onExpandRequest }) {
  return (
    <TooltipProvider delayDuration={0}>
      <nav data-testid="sidebar-navigation" className={cn('flex flex-col gap-6 py-4', collapsed ? 'px-2' : 'px-3')}>
        {nav.map((section) => (
          <div key={section.group}>
            {!collapsed && <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{section.group}</p>}
            <div className="flex flex-col gap-1">
              {section.items.map((item) => {
                if (item.children) return <ExpandableItem key={item.label} item={item} onNavigate={onNavigate} collapsed={collapsed} onExpandRequest={onExpandRequest} />;
                if (collapsed) return <CollapsedLink key={item.to} item={item} onNavigate={onNavigate} />;
                const Icon = item.icon;
                return (
                  <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} className={linkClass}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </TooltipProvider>
  );
}

function Brand({ collapsed, subtitle = 'Enterprise CRM' }) {
  return (
    <div className={cn('flex items-center gap-2.5 border-b py-5', SIDEBAR_BORDER, collapsed ? 'justify-center px-3' : 'px-5')}>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Mountain className="h-5 w-5" />
      </div>
      {!collapsed && (
        <div className="leading-tight">
          <p className="font-display text-base font-bold tracking-tight text-white">Sila Experiences</p>
          <p className="text-[11px] text-slate-400">{subtitle}</p>
        </div>
      )}
    </div>
  );
}

export function AppShell({ children, title, help }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sila_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const nav = user?.role === 'admin' ? ADMIN_NAV : user?.role === 'staff' ? STAFF_NAV : user?.role === 'investor' ? INVESTOR_NAV : buildStakeholderNav(user?.tenant_type);
  const brandSubtitle = user?.role === 'staff' ? 'Area Staff' : user?.role === 'investor' ? 'Area Partners' : user?.role === 'stakeholder' ? 'Area Riservata' : 'Enterprise CRM';
  const loginPath = user?.role === 'staff' ? '/staff/accedi' : user?.role === 'investor' ? '/investitori/accedi' : '/login';

  useEffect(() => {
    try { localStorage.setItem('sila_sidebar_collapsed', collapsed ? '1' : '0'); } catch { /* noop */ }
  }, [collapsed]);

  const handleLogout = async () => { await logout(); navigate(loginPath); };
  const asideW = collapsed ? 'lg:w-[76px]' : 'lg:w-[280px]';
  const mainPad = collapsed ? 'lg:pl-[76px]' : 'lg:pl-[280px]';

  // Guida rapida della sezione: admin (tutto il CRM) + stakeholder hotel/ristorante.
  const sectionGuide = getSectionGuide({ role: user?.role, tenantType: user?.tenant_type, pathname: location.pathname });

  return (
    <div className="sila-noise flex min-h-screen bg-background">
      <aside className={cn('hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 border-r z-20 transition-[width] duration-200', SIDEBAR_BG, SIDEBAR_TEXT, SIDEBAR_BORDER, asideW)}>
        <Brand collapsed={collapsed} subtitle={brandSubtitle} />
        <div className="flex-1 overflow-y-auto"><NavContent nav={nav} collapsed={collapsed} onExpandRequest={() => setCollapsed(false)} /></div>
        <div className={cn('border-t p-3', SIDEBAR_BORDER)}>
          <div className={cn('mb-2 flex items-center gap-2.5', collapsed ? 'justify-center px-0' : 'px-2')}>
            <UserAvatar name={user?.name || user?.email} src={user?.avatar_url} className="h-8 w-8 text-xs" fallbackClassName="bg-white/15 text-white" data-testid="appshell-user-avatar" />
            {!collapsed && (
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-white" data-testid="appshell-user-name">{user?.name}</p>
                <p className="truncate text-xs text-slate-400">{user?.email}</p>
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" className={cn('w-full gap-2 border-white/15 bg-transparent text-slate-200 hover:bg-white/10 hover:text-white', collapsed ? 'justify-center px-0' : 'justify-start')} onClick={handleLogout} data-testid="logout-button" title="Esci">
            <LogOut className="h-4 w-4" />{!collapsed && 'Esci'}
          </Button>
        </div>
      </aside>

      <div className={cn('flex min-w-0 flex-1 flex-col transition-[padding] duration-200', mainPad)}>
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-border bg-background/80 px-4 backdrop-blur sm:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="sidebar-toggle-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className={cn('w-[280px] p-0 overflow-y-auto border-r', SIDEBAR_BG, SIDEBAR_TEXT, SIDEBAR_BORDER)}>
                <Brand collapsed={false} subtitle={brandSubtitle} />
                <NavContent nav={nav} collapsed={false} onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <Button variant="ghost" size="icon" className="hidden lg:inline-flex" onClick={() => setCollapsed((c) => !c)} data-testid="sidebar-collapse-button" title={collapsed ? 'Espandi menu' : 'Riduci menu'} aria-label="Riduci o espandi il menu">
              {collapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </Button>
            <h1 className="font-display text-lg font-semibold tracking-tight sm:text-xl">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm" className="gap-2" data-testid="back-to-site-button" title="Torna al sito">
              <Link to="/"><Globe className="h-4 w-4" /><span className="hidden sm:inline">Torna al sito</span></Link>
            </Button>
            {help && <HelpButton pageKey={help} />}
            <RealtimePill />
            <LanguageSwitcher />
            <NotificationsBell />
            <UserAvatar name={user?.name || user?.email} src={user?.avatar_url} className="h-8 w-8 text-xs" data-testid="appshell-header-avatar" />
          </div>
        </header>
        <main className="relative z-10 min-w-0 max-w-full flex-1 overflow-x-hidden px-4 py-6 sm:px-6 lg:px-8">
          {sectionGuide && <SectionGuide guide={sectionGuide} storageKey={`sila_guide_${location.pathname}`} />}
          {children}
        </main>
      </div>
    </div>
  );
}
